import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import type { SearchGroups, SearchHit, SearchResponse } from "shared";
import { api } from "../api";
import { useCallerState } from "../callerState";
import { useProgramPanel } from "../programPanel";
import { whyText } from "../search/WhyMatched";
import { StatusBadge, VerificationBadge } from "./StatusBadge";

export const GROUPS: { key: keyof SearchGroups; title: string }[] = [
  { key: "problems", title: "Problems" }, { key: "programs", title: "Programs" },
  { key: "faqs", title: "FAQs" }, { key: "departments", title: "Departments" },
];

/** Pending, stale and under-review records show their badge in the list; a verified one shows none. */
const flag = (v: SearchHit["verification"]) => !!v && (v.warning || v.status === "pending");

const isTyping = (t: EventTarget | null) =>
  t instanceof HTMLElement && (t.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName));

/** Search on every screen: "/" or Ctrl+K focuses it; arrows, Enter and Esc drive the suggestions. */
export function GlobalSearch() {
  const navigate = useNavigate();
  const { state } = useCallerState();
  const panel = useProgramPanel();
  const input = useRef<HTMLInputElement>(null);
  const [text, setText] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(text), 120);
    return () => clearTimeout(t);
  }, [text]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); input.current?.focus(); input.current?.select(); return; }
      if (e.key === "/" && !e.ctrlKey && !e.metaKey && !e.altKey && !isTyping(e.target)) { e.preventDefault(); input.current?.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const ready = debounced.trim().length >= 2;
  const qs = new URLSearchParams(state ? { q: debounced, state } : { q: debounced }).toString();
  const sugg = useQuery({
    queryKey: ["suggest", debounced, state ?? ""], enabled: ready, staleTime: 0, placeholderData: keepPreviousData,
    queryFn: () => api<SearchResponse>(`/api/search/suggest?${qs}`),
  });
  const data = ready ? sugg.data : undefined;
  const options = useMemo(() => (data ? GROUPS.flatMap((g) => data.groups[g.key]) : []), [data]);
  useEffect(() => setActive(-1), [data]);
  const showList = open && !!data;

  const close = () => { setOpen(false); setActive(-1); };
  // New text drops the highlight at once: the list keeps the last results until the new ones arrive, and Enter
  // must not open one of those for different words.
  const onType = (v: string) => { setText(v); setOpen(true); setActive(-1); };
  const seeAll = () => {
    const q = text.trim();
    if (!q) return;
    close();
    navigate({ to: "/search", search: { q, state } });
  };
  const choose = (h: SearchHit) => {
    close();
    setText("");
    setDebounced("");
    const target = h.type === "faq" && h.owner ? h.owner : { type: h.type, slug: h.slug };
    // A program (or a program's FAQ) opens beside the current screen, so the rep never leaves it; the route stays.
    if (target.type === "program") panel.open(target.slug, h.stateCode ?? state);
    else if (target.type === "problem") navigate({ to: "/problems/$slug", params: { slug: target.slug }, search: { state } });
    else if (target.type === "department") navigate({ to: "/directory/departments/$slug", params: { slug: target.slug } });
  };
  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    const n = options.length + 1; // the last option is "See all results"
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((i) => (i + 1) % n); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setOpen(true); setActive((i) => (i <= 0 ? n - 1 : i - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); if (active >= 0 && active < options.length) choose(options[active]); else seeAll(); }
    // Esc closes the list when one is shown; otherwise it leaves the box and goes on to the program panel.
    else if (e.key === "Escape") { if (showList) { e.preventDefault(); close(); } else input.current?.blur(); }
  };

  return (
    <div className="relative w-full max-w-xl">
      <Search size={14} className="pointer-events-none absolute left-2.5 top-2.5 text-slate-400" />
      <input ref={input} role="combobox" aria-label="Search programs, problems and FAQs" aria-expanded={showList}
        aria-controls="search-suggestions" aria-autocomplete="list"
        aria-activedescendant={showList && active >= 0 ? `sugg-${active}` : undefined}
        className="input pl-8" placeholder="Search: what is the caller dealing with?  ( / )"
        value={text} onChange={(e) => onType(e.target.value)} onFocus={() => setOpen(true)} onBlur={close}
        onKeyDown={onKeyDown} />
      {showList && data && (
        <div id="search-suggestions" role="listbox" aria-label="Search suggestions"
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[70vh] overflow-auto rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg dark:border-slate-700 dark:bg-slate-900">
          {data.query.corrections.length > 0 && (
            <div className="px-3 py-1 text-xs text-slate-500">Showing results for {data.query.corrections.map((c) => `“${c.to}”`).join(", ")}</div>
          )}
          {GROUPS.map((g) => {
            const hits = data.groups[g.key];
            if (!hits.length) return null;
            return (
              <div key={g.key} role="group" aria-label={g.title}>
                <div className="px-3 pt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{g.title}</div>
                {hits.map((h) => {
                  const i = options.indexOf(h);
                  const sub = [h.subtitle, whyText(h.why)].filter(Boolean).join(" · ");
                  return (
                    <div key={`${h.type}-${h.id}`} id={`sugg-${i}`} role="option" aria-selected={i === active}
                      className={`cursor-pointer px-3 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-800 ${i === active ? "bg-brand-50 dark:bg-slate-800" : ""}`}
                      onMouseDown={(e) => { e.preventDefault(); choose(h); }}>
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium">{h.title}</span>
                        {h.status && h.status !== "active" && <StatusBadge status={h.status} />}
                        {flag(h.verification) && <VerificationBadge v={h.verification!} />}
                        {/* A state version's badge carries its state, as on the program page. */}
                        {flag(h.stateVerification) && (
                          <span className="whitespace-nowrap text-xs text-slate-500">{h.stateCode}: <VerificationBadge v={h.stateVerification!} /></span>
                        )}
                      </div>
                      {sub && <div className="truncate text-xs text-slate-500">{sub}</div>}
                    </div>
                  );
                })}
              </div>
            );
          })}
          {options.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">No quick matches.</div>}
          <div id={`sugg-${options.length}`} role="option" aria-selected={active === options.length}
            className={`cursor-pointer border-t border-slate-200 px-3 py-1.5 text-brand-700 hover:bg-slate-50 dark:border-slate-700 dark:text-brand-100 dark:hover:bg-slate-800 ${active === options.length ? "bg-brand-50 dark:bg-slate-800" : ""}`}
            onMouseDown={(e) => { e.preventDefault(); seeAll(); }}>
            See all results for “{text.trim()}”
          </div>
        </div>
      )}
    </div>
  );
}
